// src/hooks/auth/mfaUtils.js
// ---------------------------------------------------------------------------
// Purpose: Isolate MFA-specific helper flows (build resolver, send SMS, detect
// TOTP hints) so the main sign-in hook remains readable and focused on UI
// state transitions.
//
// TOTP support (Jan 2025):
// We added TOTP (Time-based One-Time Password) MFA alongside the existing SMS
// MFA to support automated RPA bot sign-in. TOTP does not require reCAPTCHA,
// which allows bots to complete MFA without solving CAPTCHAs. Human users
// continue to use SMS MFA as before — the MFA type is determined at sign-in by
// inspecting which factors the user has enrolled, not by any UI toggle.
// ---------------------------------------------------------------------------

import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  TotpMultiFactorGenerator,
} from 'firebase/auth';

/**
 * Build the MultiFactorResolver from a caught auth/multi-factor-auth-required error.
 * This is the same for both SMS and TOTP — the resolver contains hints about
 * which MFA factors the user has enrolled.
 */
export function buildMfaResolver(auth, error) {
  return getMultiFactorResolver(auth, error);
}

// ---------------------------------------------------------------------------
// MFA type detection helpers
// ---------------------------------------------------------------------------

/**
 * Determine which MFA method to use based on the resolver's enrolled factor hints.
 *
 * Priority:
 *   1. If the user has a TOTP factor enrolled → use TOTP (no reCAPTCHA needed)
 *   2. Otherwise fall back to phone/SMS
 *
 * This ordering intentionally favours TOTP because it avoids reCAPTCHA entirely,
 * which is the whole reason we added TOTP for the RPA bot. For regular human
 * users, only phone hints will be present (they never enroll in TOTP), so this
 * function naturally returns 'phone' for them.
 *
 * @param {object} resolver - The MultiFactorResolver from Firebase
 * @returns {'totp' | 'phone'} The MFA method to use
 */
export function determineMfaType(resolver) {
  const hints = resolver?.hints || [];
  const hasTotpHint = hints.some(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
  );
  return hasTotpHint ? 'totp' : 'phone';
}

/**
 * Find the first TOTP hint in the resolver's enrolled factors.
 * Returns the hint object (which includes `uid` needed for assertion), or null.
 *
 * @param {object} resolver - The MultiFactorResolver from Firebase
 * @returns {object|null} The TOTP hint, or null if none found
 */
export function getTotpHint(resolver) {
  const hints = resolver?.hints || [];
  return (
    hints.find(
      (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
    ) || null
  );
}

/**
 * Find the first phone/SMS hint in the resolver's enrolled factors.
 * Returns the hint object, or null.
 *
 * @param {object} resolver - The MultiFactorResolver from Firebase
 * @returns {object|null} The phone hint, or null if none found
 */
export function getPhoneHint(resolver) {
  const hints = resolver?.hints || [];
  return (
    hints.find(
      (h) => h.factorId === 'phone' // PhoneMultiFactorGenerator.FACTOR_ID is 'phone'
    ) || null
  );
}

// ---------------------------------------------------------------------------
// SMS MFA helpers (existing)
// ---------------------------------------------------------------------------

/**
 * Send the SMS verification code using the resolver session and the provided
 * RecaptchaVerifier. This is only used for phone/SMS MFA — TOTP does not need
 * a verification step from our side (the user generates the code themselves).
 *
 * @returns {{ verificationId: string, maskedPhone: string }}
 */
export async function sendMfaCode(auth, resolver, recaptchaVerifier) {
  const phoneHint = getPhoneHint(resolver);
  if (!phoneHint) {
    throw new Error('No phone MFA hint found on resolver');
  }
  const provider = new PhoneAuthProvider(auth);
  const verificationId = await provider.verifyPhoneNumber(
    { multiFactorHint: phoneHint, session: resolver.session },
    recaptchaVerifier
  );
  // Mask the phone number for display (show only last 4 digits)
  const full = phoneHint?.phoneNumber || '';
  const last4 = full.replace(/\D/g, '').slice(-4);
  const maskedPhone = last4 ? `••••${last4}` : '';
  return { verificationId, maskedPhone };
}

// ---------------------------------------------------------------------------
// TOTP MFA helpers (new)
// ---------------------------------------------------------------------------

/**
 * Build a TOTP assertion for sign-in.
 *
 * Unlike SMS MFA which requires a verificationId from Firebase (proving we sent
 * the code), TOTP just needs the hint's uid and the 6-digit code the user
 * generated from their authenticator app / TOTP library.
 *
 * No reCAPTCHA is involved in this flow — this is the key advantage for bots.
 *
 * @param {string} totpHintUid - The uid from the TOTP hint in resolver.hints
 * @param {string} otpCode - The 6-digit TOTP code
 * @returns {object} A TotpMultiFactorAssertion to pass to resolver.resolveSignIn()
 */
export function buildTotpAssertion(totpHintUid, otpCode) {
  return TotpMultiFactorGenerator.assertionForSignIn(totpHintUid, otpCode);
}
